'use client'

import { useEffect, useMemo, useState } from 'react'

// import { ChevronDown, Clock } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'

import { cn, isFieldInvalid } from '@/utils'

import { FieldWrapper, FieldWrapperClasses } from './field-wrapper'
import { useFieldContext } from './form'

type TimeParts = {
  hours: number
  minutes: number
  seconds: number
}

const HOURS_12 = Array.from({ length: 12 }, (_value, index) => index + 1)
const AMPM_OPTIONS = ['AM', 'PM']

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const pad = (value: number) => value.toString().padStart(2, '0')

const parseTime = (value: null | string | undefined): null | TimeParts => {
  if (!value) return null

  // Handle ISO date strings (e.g., "2024-01-15T14:30:00Z" or "2024-01-15T14:30:00.000Z")
  if (value.includes('T')) {
    try {
      const date = new Date(value)
      if (!Number.isNaN(date.getTime())) {
        return {
          hours: date.getHours(),
          minutes: date.getMinutes(),
          seconds: date.getSeconds()
        }
      }
    } catch {
      // Fall through to time string parsing
    }
  }

  // Handle time strings (e.g., "14:30:00" or "14:30")
  const segments = value.split(':').map((segment) => segment.trim())
  if (segments.length < 2) return null

  const [hours, minutes, seconds = '0'] = segments
  const parsedHours = Number.parseInt(hours, 10)
  const parsedMinutes = Number.parseInt(minutes, 10)
  const parsedSeconds = Number.parseInt(seconds, 10)

  if (
    Number.isNaN(parsedHours) ||
    Number.isNaN(parsedMinutes) ||
    Number.isNaN(parsedSeconds)
  ) {
    return null
  }

  if (
    parsedHours < 0 ||
    parsedHours > 23 ||
    parsedMinutes < 0 ||
    parsedMinutes > 59 ||
    parsedSeconds < 0 ||
    parsedSeconds > 59
  ) {
    return null
  }

  return {
    hours: parsedHours,
    minutes: parsedMinutes,
    seconds: parsedSeconds
  }
}

const formatValue = ({ hours, minutes, seconds }: TimeParts) =>
  `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`

const to12Hour = (hours24: number): { ampm: 'AM' | 'PM'; hours12: number } => {
  if (hours24 === 0) return { ampm: 'AM', hours12: 12 }
  if (hours24 < 12) return { ampm: 'AM', hours12: hours24 }
  if (hours24 === 12) return { ampm: 'PM', hours12: 12 }
  return { ampm: 'PM', hours12: hours24 - 12 }
}

const to24Hour = (hours12: number, ampm: 'AM' | 'PM'): number => {
  if (ampm === 'AM') {
    return hours12 === 12 ? 0 : hours12
  }
  return hours12 === 12 ? 12 : hours12 + 12
}

const formatDisplay = (
  { hours, minutes }: TimeParts,
  format?: (value: TimeParts) => string
) => {
  if (format) return format({ hours, minutes, seconds: 0 })
  const { ampm, hours12 } = to12Hour(hours)
  // Always display without seconds in UI
  return `${pad(hours12)}:${pad(minutes)} ${ampm}`
}

type PickerColumnProps = {
  label: string
  onSelect: (value: string) => void
  options: string[]
  selected?: string
}

const PickerColumn = ({
  label,
  onSelect,
  options,
  selected
}: PickerColumnProps) => {
  return (
    <div className="flex min-w-[96px] flex-1 flex-col">
      <div className="border-border/80 text-muted-foreground/80 border-b px-3 py-2 text-xs font-medium tracking-wide uppercase">
        {label}
      </div>
      <ScrollArea className="h-56">
        <div className="space-y-1 p-2">
          {options.map((option) => {
            const isSelected = option === selected
            return (
              <button
                className={cn(
                  'focus-visible:ring-ring w-full rounded-md px-2 py-1.5 text-start text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                  isSelected
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-muted hover:text-foreground/90'
                )}
                data-state={isSelected ? 'selected' : undefined}
                key={option}
                onClick={() => onSelect(option)}
                type="button"
              >
                <span className="font-mono">{option}</span>
              </button>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}

export type TimePickerProps = {
  children?: React.ReactNode
  classNames?: FieldWrapperClasses
  clearable?: boolean
  contentClassName?: string
  disabled?: boolean
  format?: (value: TimeParts) => string
  label?: string
  minuteStep?: number
  placeholder?: string
  required?: boolean
  showNowButton?: boolean
  triggerClassName?: string
  withSeconds?: boolean
}

export const TimePicker = ({
  children,
  classNames,
  clearable = true,
  disabled,
  format,
  label,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  minuteStep: _minuteStep = 5,
  placeholder,
  required = false,
  showNowButton = true,
  withSeconds = false
}: TimePickerProps) => {
  const t = useTranslations('components.form.timePicker')
  const field = useFieldContext<null | string | undefined>()
  const isInvalid = isFieldInvalid(field)
  const [open, setOpen] = useState(false)
  const defaultPlaceholder = placeholder ?? t('placeholder')

  // Convert ISO date strings to time format (HH:mm:ss)
  useEffect(() => {
    const value = field.state.value
    if (value && typeof value === 'string' && value.includes('T')) {
      try {
        const date = new Date(value)
        if (!Number.isNaN(date.getTime())) {
          const timeString = formatValue({
            hours: date.getHours(),
            minutes: date.getMinutes(),
            seconds: date.getSeconds()
          })
          // Only update if the value is different to avoid infinite loops
          if (value !== timeString) {
            field.handleChange(timeString)
          }
        }
      } catch {
        // Ignore parsing errors
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.state.value])

  const parsedValue = useMemo(
    () => parseTime(field.state.value),
    [field.state.value]
  )

  const minuteOptions = useMemo(
    () => Array.from({ length: 60 }, (_value, index) => pad(index)),
    []
  )

  const { ampm: currentAmpm, hours12: currentHours12 } = useMemo(
    () =>
      parsedValue
        ? to12Hour(parsedValue.hours)
        : { ampm: 'AM' as const, hours12: 12 },
    [parsedValue]
  )

  const displayValue = parsedValue
    ? formatDisplay(parsedValue, format)
    : defaultPlaceholder

  const handleCommit = (
    next: { ampm?: 'AM' | 'PM'; hours12?: number } & Partial<TimeParts>,
    closePopover = false
  ) => {
    const base = parsedValue ?? { hours: 0, minutes: 0, seconds: 0 }
    const { ampm: baseAmpm, hours12: baseHours12 } = to12Hour(base.hours)

    // Convert 12-hour selection to 24-hour for storage
    const hours12 = next.hours12 ?? baseHours12
    const ampm = next.ampm ?? baseAmpm
    const hours24 = to24Hour(hours12, ampm)

    const merged: TimeParts = {
      hours: hours24,
      minutes: clamp(next.minutes ?? base.minutes, 0, 59),
      seconds: withSeconds ? clamp(next.seconds ?? base.seconds, 0, 59) : 0
    }

    field.handleChange(formatValue(merged))

    if (closePopover) {
      setOpen(false)
    }
  }

  const handleClear = () => {
    field.handleChange(undefined)
    setOpen(false)
  }

  const handleNow = () => {
    const current = new Date()
    handleCommit(
      {
        hours: current.getHours(),
        minutes: current.getMinutes(),
        seconds: withSeconds ? current.getSeconds() : 0
      },
      true
    )
  }

  return (
    <FieldWrapper
      classNames={classNames}
      field={field}
      label={label}
      required={required}
    >
      <Popover
        onOpenChange={(value) => {
          setOpen(value)
          if (!value) {
            field.handleBlur()
          }
        }}
        open={open}
      >
        <PopoverTrigger asChild>
          <Button
            aria-invalid={isInvalid || undefined}
            aria-required={required || undefined}
            className={cn(
              'flex h-[42px] !w-full items-center justify-between gap-3 rounded-md px-3.5',
              !parsedValue && 'text-muted-foreground'
            )}
            data-invalid={isInvalid || undefined}
            disabled={disabled}
            id={field.name}
            mode="input"
            onBlur={field.handleBlur}
            type="button"
            variant="outline"
            wrapChildren={false}
          >
            <span>{displayValue}</span>
            {children}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[320px] max-w-[384px] p-0">
          <div className="divide-border grid grid-cols-3 divide-x border-b">
            <PickerColumn
              label={t('hours')}
              onSelect={(value) => handleCommit({ hours12: Number(value) })}
              options={HOURS_12.map(pad)}
              selected={parsedValue ? pad(currentHours12) : undefined}
            />
            <PickerColumn
              label={t('minutes')}
              onSelect={(value) =>
                handleCommit({ minutes: Number(value) }, true)
              }
              options={minuteOptions}
              selected={parsedValue ? pad(parsedValue.minutes) : undefined}
            />
            <PickerColumn
              label={t('ampm')}
              onSelect={(value) =>
                handleCommit({ ampm: value as 'AM' | 'PM' }, true)
              }
              options={AMPM_OPTIONS}
              selected={parsedValue ? currentAmpm : undefined}
            />
          </div>

          {(clearable || showNowButton) && (
            <div className="flex items-center justify-end gap-2 px-3 py-2">
              {showNowButton && (
                <Button
                  onClick={handleNow}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  {t('now')}
                </Button>
              )}
              {clearable && (
                <Button
                  onClick={handleClear}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  {t('clear')}
                </Button>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </FieldWrapper>
  )
}
